import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { ArrowPathIcon, Bars3Icon, PencilSquareIcon, TrashIcon } from "@heroicons/react/24/outline";
import { useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Selecto from "react-selecto";

import { Button, Dialog, Small, Tooltip } from "@fronterahealth/frontera-ui-components";

import {
  GoalDomainAssignmentInput,
  ManageReportDomainInput,
  ReportGoalType,
  useGetReportDomainsQuery,
  useManageReportDomainsMutation,
} from "@api/graphql/types-and-hooks";
import { DomainDescription } from "@components/DomainDescription/DomainDescription";
import { notifyError, notifySuccess } from "@components/notifications/notifications";
import {
  ConfirmChangesDialog,
  DomainChangeSummary,
} from "@pages/ClinicalReportDetails/ReportPage/Components/ManageDomains/ConfirmChangesDialog";
import { DomainEditDialog } from "@pages/ClinicalReportDetails/ReportPage/Components/ManageDomains/DomainEditDialog";

interface ManageDomainsDialogProps {
  open: boolean;
  onClose: () => void;
  reportId: string;
  goalList: ReportGoalType[];
  /** Optional caller-side refresh after a successful save (e.g. refetch the
   *  page's own goal query, since the dialog only invalidates report-domains). */
  onSuccess?: () => Promise<void> | void;
}

/** Internal sentinel for the bucket of goals not assigned to any domain. */
const UNCATEGORIZED_KEY = "__uncategorized__";

/** Internal prefix used to refer to a brand-new (unsaved) domain in the
 *  goal-assignment map. The portion after `c:` is the domain's client_id. */
const NEW_DOMAIN_PREFIX = "c:";

/** Internal prefix that distinguishes a domain-reorder drag from a goal-move
 *  drag inside the shared DndContext. `useSortable`'s id is namespaced with
 *  this so `handleDragEnd` can branch cleanly on the `active.id` form. */
const DOMAIN_DRAG_PREFIX = "domain-";

type DomainKey = string; // existing domain id | `c:<clientId>` | UNCATEGORIZED_KEY

interface PendingExistingDomain {
  kind: "existing";
  id: string;
  name: string;
  description: string;
  originalName: string;
  originalDescription: string;
}

interface PendingNewDomain {
  kind: "new";
  clientId: string;
  name: string;
  description: string;
}

type PendingDomain = PendingExistingDomain | PendingNewDomain;

const newClientId = () => `nd-${Math.random().toString(36).slice(2, 10)}`;

const domainKey = (d: PendingDomain): DomainKey => (d.kind === "existing" ? d.id : `${NEW_DOMAIN_PREFIX}${d.clientId}`);

const sortableDomainId = (d: PendingDomain) => `${DOMAIN_DRAG_PREFIX}${domainKey(d)}`;

const goalDisplayName = (g: ReportGoalType) => g.goalName || "Untitled goal";

export const ManageDomainsDialog: React.FC<ManageDomainsDialogProps> = ({
  open,
  onClose,
  reportId,
  goalList,
  onSuccess,
}) => {
  const queryClient = useQueryClient();

  const { data: reportDomainsData, isLoading: domainsLoading } = useGetReportDomainsQuery(
    { reportId },
    { enabled: open && !!reportId },
  );
  const reportDomains = useMemo(() => reportDomainsData?.getReportDomains ?? [], [reportDomainsData]);

  const [domains, setDomains] = useState<PendingDomain[]>([]);
  const [deletedExistingIds, setDeletedExistingIds] = useState<string[]>([]);
  const [goalAssignments, setGoalAssignments] = useState<Map<string, DomainKey>>(new Map());
  const [originalAssignments, setOriginalAssignments] = useState<Map<string, DomainKey>>(new Map());
  const [selectedGoalIds, setSelectedGoalIds] = useState<Set<string>>(new Set());
  const [lastClickedGoalId, setLastClickedGoalId] = useState<string | null>(null);

  const [editingDomainKey, setEditingDomainKey] = useState<DomainKey | null>(null);
  const [isCreatingDomain, setIsCreatingDomain] = useState(false);
  const [showConfirmChanges, setShowConfirmChanges] = useState(false);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);

  const [activeDragGoalId, setActiveDragGoalId] = useState<string | null>(null);
  const listContainerRef = useRef<HTMLDivElement | null>(null);
  const selectoRef = useRef<Selecto | null>(null);

  const manageMutation = useManageReportDomainsMutation({});
  const isPending = manageMutation.isPending;

  // Build a stable id → goal lookup based on the prop's snapshot.
  const goalsById = useMemo(() => {
    const map = new Map<string, ReportGoalType>();
    for (const g of goalList) map.set(g.id, g);
    return map;
  }, [goalList]);

  // Re-seed local state every time the dialog opens.
  useEffect(() => {
    if (!open) return;
    setDomains(
      reportDomains.map((d) => ({
        kind: "existing",
        id: d.id,
        name: d.name ?? "",
        description: d.description ?? "",
        originalName: d.name ?? "",
        originalDescription: d.description ?? "",
      })),
    );
    setDeletedExistingIds([]);
    const initialAssignments = new Map<string, DomainKey>();
    for (const g of goalList) {
      initialAssignments.set(g.id, g.reportDomain?.id ?? UNCATEGORIZED_KEY);
    }
    setGoalAssignments(initialAssignments);
    setOriginalAssignments(new Map(initialAssignments));
    setSelectedGoalIds(new Set());
    setLastClickedGoalId(null);
    setEditingDomainKey(null);
    setIsCreatingDomain(false);
    setShowConfirmChanges(false);
    setShowDiscardConfirm(false);
    setActiveDragGoalId(null);
  }, [open, reportDomains, goalList]);

  // Bucket goals by their CURRENT (pending) assignment.
  const goalsByDomainKey = useMemo(() => {
    const buckets = new Map<DomainKey, ReportGoalType[]>();
    buckets.set(UNCATEGORIZED_KEY, []);
    for (const d of domains) buckets.set(domainKey(d), []);
    for (const [goalId, key] of goalAssignments.entries()) {
      const goal = goalsById.get(goalId);
      if (!goal) continue;
      const bucket = buckets.get(key) ?? buckets.get(UNCATEGORIZED_KEY)!;
      bucket.push(goal);
    }
    return buckets;
  }, [domains, goalAssignments, goalsById]);

  // Flat array of goal ids in render order — used for shift-range selection.
  const orderedGoalIds = useMemo(() => {
    const ordered: string[] = [];
    const keys: DomainKey[] = [UNCATEGORIZED_KEY, ...domains.map(domainKey)];
    for (const key of keys) {
      const bucket = goalsByDomainKey.get(key) ?? [];
      for (const g of bucket) ordered.push(g.id);
    }
    return ordered;
  }, [domains, goalsByDomainKey]);

  const hasChanges = useMemo(() => {
    if (deletedExistingIds.length > 0) return true;
    for (const d of domains) {
      if (d.kind === "new") return true;
      if (d.name !== d.originalName || d.description !== d.originalDescription) return true;
    }
    const existingOrder = domains.filter((d): d is PendingExistingDomain => d.kind === "existing").map((d) => d.id);
    const originalOrder = [...reportDomains]
      .sort((a, b) => (a.rankOrder ?? 0) - (b.rankOrder ?? 0))
      .filter((rd) => !deletedExistingIds.includes(rd.id))
      .map((rd) => rd.id);
    if (existingOrder.join(",") !== originalOrder.join(",")) return true;
    for (const [goalId, key] of goalAssignments.entries()) {
      if (originalAssignments.get(goalId) !== key) return true;
    }
    return false;
  }, [domains, deletedExistingIds, goalAssignments, originalAssignments, reportDomains]);

  // ── Selection helpers ──────────────────────────────────────────────────
  const replaceSelection = useCallback((ids: string[]) => {
    setSelectedGoalIds(new Set(ids));
  }, []);

  const toggleInSelection = useCallback((id: string) => {
    setSelectedGoalIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleGoalClick = useCallback(
    (e: React.MouseEvent, goalId: string) => {
      if (e.shiftKey && lastClickedGoalId) {
        const a = orderedGoalIds.indexOf(lastClickedGoalId);
        const b = orderedGoalIds.indexOf(goalId);
        if (a >= 0 && b >= 0) {
          const [lo, hi] = a < b ? [a, b] : [b, a];
          replaceSelection(orderedGoalIds.slice(lo, hi + 1));
        }
      } else if (e.metaKey || e.ctrlKey) {
        toggleInSelection(goalId);
        setLastClickedGoalId(goalId);
      } else {
        replaceSelection([goalId]);
        setLastClickedGoalId(goalId);
      }
    },
    [lastClickedGoalId, orderedGoalIds, replaceSelection, toggleInSelection],
  );

  // ── Domain CRUD ────────────────────────────────────────────────────────
  const handleSubmitEdit = (key: DomainKey, name: string, description: string) => {
    setDomains((prev) =>
      prev.map((d) => {
        if (domainKey(d) !== key) return d;
        return { ...d, name, description };
      }),
    );
    setEditingDomainKey(null);
  };

  const handleCreateDomain = (name: string, description: string) => {
    const clientId = newClientId();
    setDomains((prev) => [...prev, { kind: "new", clientId, name, description }]);
    setIsCreatingDomain(false);
  };

  const handleDeleteDomain = (key: DomainKey) => {
    const target = domains.find((d) => domainKey(d) === key);
    if (!target) return;
    if (target.kind === "existing") {
      setDeletedExistingIds((prev) => [...prev, target.id]);
    }
    setDomains((prev) => prev.filter((d) => domainKey(d) !== key));
  };

  // ── Drag-and-drop ──────────────────────────────────────────────────────
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

  const handleDragStart = (event: DragStartEvent) => {
    const activeId = String(event.active.id);
    if (activeId.startsWith(DOMAIN_DRAG_PREFIX)) {
      // Domain reorder drag — no need to track for the overlay; SortableContext
      // animates the section in place.
      return;
    }
    setActiveDragGoalId(activeId);
    if (!selectedGoalIds.has(activeId)) {
      replaceSelection([activeId]);
      setLastClickedGoalId(activeId);
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveDragGoalId(null);
    const activeId = String(event.active.id);
    const overId = event.over ? String(event.over.id) : null;
    if (!overId) return;

    // Domain reorder: activeId carries the `domain-` prefix from useSortable.
    // overId may resolve to either the outer sortable wrapper (`domain-<key>`) or the
    // inner useDroppable (`<key>`) since both share the same rect — normalize to
    // the prefixed form so the reorder lands either way.
    if (activeId.startsWith(DOMAIN_DRAG_PREFIX)) {
      const normalizedOverId = overId.startsWith(DOMAIN_DRAG_PREFIX) ? overId : `${DOMAIN_DRAG_PREFIX}${overId}`;
      if (normalizedOverId === activeId) return;
      setDomains((prev) => {
        const ids = prev.map(sortableDomainId);
        const oldIndex = ids.indexOf(activeId);
        const newIndex = ids.indexOf(normalizedOverId);
        if (oldIndex < 0 || newIndex < 0) return prev;
        return arrayMove(prev, oldIndex, newIndex);
      });
      return;
    }

    // Goal move: overId is a domain key (Uncategorized, existing domain id, or `c:<clientId>`).
    // If rectIntersection resolved to the SortableDomainSection wrapper (same DOM rect as
    // the inner useDroppable), strip the prefix so the goal lands in the intended domain
    // rather than being orphaned with an invalid `domain-…` key.
    const destKey = overId.startsWith(DOMAIN_DRAG_PREFIX) ? overId.slice(DOMAIN_DRAG_PREFIX.length) : overId;
    const ids = new Set(selectedGoalIds);
    if (ids.size === 0) ids.add(activeId);
    setGoalAssignments((prev) => {
      const next = new Map(prev);
      for (const id of ids) next.set(id, destKey);
      return next;
    });
  };

  // ── Save flow ──────────────────────────────────────────────────────────
  const buildChangeSummary = useCallback((): DomainChangeSummary[] => {
    const summaries: DomainChangeSummary[] = [];
    const allKeys: DomainKey[] = [UNCATEGORIZED_KEY, ...domains.map(domainKey)];
    const deletedRecords = reportDomains.filter((d) => deletedExistingIds.includes(d.id));
    const keysIncludingDeleted: DomainKey[] = [...allKeys, ...deletedRecords.map((d) => d.id)];

    for (const key of keysIncludingDeleted) {
      const domain = domains.find((d) => domainKey(d) === key);
      const deletedRecord = deletedRecords.find((d) => d.id === key);
      const isDeleted = !!deletedRecord;
      const label =
        key === UNCATEGORIZED_KEY
          ? "Uncategorized"
          : domain
            ? domain.name || "(unnamed)"
            : deletedRecord?.name ?? "(deleted)";
      const isNew = domain?.kind === "new";

      const metaChanges: string[] = [];
      if (domain?.kind === "existing") {
        if (domain.name !== domain.originalName) {
          metaChanges.push(`Renamed from "${domain.originalName}" to "${domain.name}"`);
        }
        if (domain.description !== domain.originalDescription) {
          metaChanges.push("Description changed");
        }
      }

      const removed: string[] = [];
      const added: string[] = [];
      for (const [goalId, currentKey] of goalAssignments.entries()) {
        const origKey = originalAssignments.get(goalId) ?? UNCATEGORIZED_KEY;
        if (origKey === currentKey) continue;
        const goal = goalsById.get(goalId);
        if (!goal) continue;
        if (origKey === key) removed.push(goalDisplayName(goal));
        if (currentKey === key) added.push(goalDisplayName(goal));
      }

      const isUncategorized = key === UNCATEGORIZED_KEY;
      const hasAnyChange = isNew || isDeleted || metaChanges.length > 0 || removed.length > 0 || added.length > 0;
      if (!hasAnyChange) continue;
      // Uncategorized has nothing to "create" / "delete" / rename — only goal moves matter.
      if (isUncategorized && removed.length === 0 && added.length === 0) continue;

      summaries.push({
        key,
        label,
        isNew,
        isDeleted,
        metaChanges,
        removedGoals: removed,
        addedGoals: added,
      });
    }
    return summaries;
  }, [domains, deletedExistingIds, goalAssignments, originalAssignments, goalsById, reportDomains]);

  const changeSummary = useMemo(
    () => (showConfirmChanges ? buildChangeSummary() : []),
    [showConfirmChanges, buildChangeSummary],
  );

  const handleAttemptSave = () => {
    if (!hasChanges) {
      onClose();
      return;
    }
    const missingName = domains.find((d) => !d.name.trim());
    if (missingName) {
      notifyError("Every domain needs a name.");
      return;
    }
    if (buildChangeSummary().length === 0) {
      // Order-only changes have nothing to surface in the diff dialog — skip it
      // and save directly. (`hasChanges` is true because reorder was detected.)
      void handleConfirmSave();
      return;
    }
    setShowConfirmChanges(true);
  };

  const buildMutationVariables = () => {
    const mutationDomains: ManageReportDomainInput[] = domains.map((d) =>
      d.kind === "existing"
        ? {
            id: d.id,
            name: d.name.trim(),
            description: d.description.trim() || undefined,
          }
        : {
            clientId: d.clientId,
            name: d.name.trim(),
            description: d.description.trim() || undefined,
          },
    );

    const goalMoves: GoalDomainAssignmentInput[] = [];
    for (const [goalId, currentKey] of goalAssignments.entries()) {
      const origKey = originalAssignments.get(goalId) ?? UNCATEGORIZED_KEY;
      if (origKey === currentKey) continue;
      if (currentKey === UNCATEGORIZED_KEY) {
        goalMoves.push({ goalId });
      } else if (currentKey.startsWith(NEW_DOMAIN_PREFIX)) {
        goalMoves.push({
          goalId,
          destinationClientId: currentKey.slice(NEW_DOMAIN_PREFIX.length),
        });
      } else {
        goalMoves.push({ goalId, destinationDomainId: currentKey });
      }
    }

    return {
      reportId,
      domains: mutationDomains,
      deletedDomainIds: deletedExistingIds,
      goalMoves,
    };
  };

  const handleConfirmSave = async () => {
    try {
      const result = await manageMutation.mutateAsync(buildMutationVariables());
      if (result.manageReportDomains?.status) {
        notifySuccess(result.manageReportDomains.message || "Domains updated.");
        await queryClient.invalidateQueries({ queryKey: ["GetReportDomains", { reportId }] });
        await queryClient.invalidateQueries({ queryKey: ["get-reassessment-goals", reportId] });
        await onSuccess?.();
        setShowConfirmChanges(false);
        onClose();
      } else {
        notifyError(result.manageReportDomains?.message || "Failed to update domains.");
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to update domains.";
      notifyError(message);
    }
  };

  const handleCancelClick = () => {
    if (hasChanges) setShowDiscardConfirm(true);
    else onClose();
  };

  const editingDomain = useMemo(
    () => (editingDomainKey ? domains.find((d) => domainKey(d) === editingDomainKey) : undefined),
    [domains, editingDomainKey],
  );

  return (
    <>
      <Dialog
        open={open}
        setOpen={() => {}}
        title="Manage domains"
        size="4xl"
        showDividers
        showCloseButton={false}
        primaryButton={{
          text: isPending ? "Saving..." : "Save",
          onClick: handleAttemptSave,
          disabled: isPending || domainsLoading,
        }}
        secondaryButton={{ text: "Cancel", onClick: handleCancelClick, disabled: isPending }}
      >
        {isPending && (
          <div className="mb-2 flex items-center gap-2 text-text-secondary">
            <ArrowPathIcon className="h-4 w-4 animate-spin" />
            <Small colorType="secondary">Applying changes…</Small>
          </div>
        )}

        {domainsLoading ? (
          <Small colorType="secondary">Loading domains…</Small>
        ) : (
          <div className="flex flex-col gap-3">
            <div className="flex items-center justify-between">
              <Small colorType="secondary">
                Drag goals between domains. Click to select; shift-click or drag a lasso to select multiple.
              </Small>
              <Button text="+ Add domain" appearance="link" onClick={() => setIsCreatingDomain(true)} />
            </div>

            <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
              <Selecto
                ref={selectoRef}
                dragContainer={listContainerRef.current ?? undefined}
                selectableTargets={[".manage-domains-goal"]}
                selectByClick={false}
                selectFromInside={false}
                continueSelect={false}
                toggleContinueSelect={["shift"]}
                hitRate={0}
                onDragStart={(e) => {
                  // Don't start a lasso when the user is grabbing a domain reorder
                  // handle — that mousedown belongs to @dnd-kit's PointerSensor.
                  const target = e.inputEvent?.target as HTMLElement | null;
                  if (target && target.closest("[data-manage-domains-grip]")) {
                    e.stop();
                  }
                }}
                onSelect={(e) => {
                  const ids = e.selected.map((el) => (el as HTMLElement).dataset.goalId).filter(Boolean) as string[];
                  replaceSelection(ids);
                }}
              />

              <div
                ref={listContainerRef}
                className="max-h-[60vh] space-y-3 overflow-y-auto pr-1"
                onClick={(e) => {
                  // Clicks on the container background (not on a goal/header/button) clear selection.
                  if (e.target === e.currentTarget) {
                    replaceSelection([]);
                    setLastClickedGoalId(null);
                  }
                }}
              >
                <DomainSection
                  domainKey={UNCATEGORIZED_KEY}
                  label="Uncategorized"
                  description="Goals not yet assigned to any domain."
                  goals={goalsByDomainKey.get(UNCATEGORIZED_KEY) ?? []}
                  selectedGoalIds={selectedGoalIds}
                  onGoalClick={handleGoalClick}
                  activeDragGoalId={activeDragGoalId}
                />
                <SortableContext items={domains.map(sortableDomainId)} strategy={verticalListSortingStrategy}>
                  {domains.map((d) => {
                    const key = domainKey(d);
                    const goals = goalsByDomainKey.get(key) ?? [];
                    return (
                      <SortableDomainSection
                        key={key}
                        sortableId={sortableDomainId(d)}
                        domainKey={key}
                        label={d.name || "(unnamed)"}
                        description={d.description}
                        isNew={d.kind === "new"}
                        goals={goals}
                        selectedGoalIds={selectedGoalIds}
                        onGoalClick={handleGoalClick}
                        activeDragGoalId={activeDragGoalId}
                        onEdit={() => setEditingDomainKey(key)}
                        onDelete={() => handleDeleteDomain(key)}
                        deleteDisabled={goals.length > 0}
                      />
                    );
                  })}
                </SortableContext>
              </div>

              <DragOverlay>
                {activeDragGoalId && goalsById.get(activeDragGoalId) ? (
                  <DragOverlayCard
                    primaryGoalName={goalDisplayName(goalsById.get(activeDragGoalId)!)}
                    count={selectedGoalIds.has(activeDragGoalId) ? selectedGoalIds.size : 1}
                  />
                ) : null}
              </DragOverlay>
            </DndContext>
          </div>
        )}
      </Dialog>

      {(editingDomain || isCreatingDomain) && (
        <DomainEditDialog
          open
          initialName={editingDomain?.name ?? ""}
          initialDescription={editingDomain?.description ?? ""}
          title={isCreatingDomain ? "Add domain" : "Edit domain"}
          primaryLabel={isCreatingDomain ? "Add" : "Save"}
          onClose={() => {
            setEditingDomainKey(null);
            setIsCreatingDomain(false);
          }}
          onSubmit={({ name, description }) => {
            if (isCreatingDomain) handleCreateDomain(name, description);
            else if (editingDomainKey) handleSubmitEdit(editingDomainKey, name, description);
          }}
        />
      )}

      {showConfirmChanges && (
        <ConfirmChangesDialog
          open
          isPending={isPending}
          summary={changeSummary}
          onCancel={() => setShowConfirmChanges(false)}
          onConfirm={handleConfirmSave}
        />
      )}

      {showDiscardConfirm && (
        <Dialog
          open
          setOpen={() => {}}
          title="Discard unsaved changes?"
          size="md"
          showCloseButton={false}
          primaryButton={{
            text: "Discard",
            onClick: () => {
              setShowDiscardConfirm(false);
              onClose();
            },
          }}
          secondaryButton={{
            text: "Keep editing",
            onClick: () => setShowDiscardConfirm(false),
          }}
        >
          <Small colorType="secondary">You have unsaved domain and goal changes. Discard them?</Small>
        </Dialog>
      )}
    </>
  );
};

// ── Sub-components ────────────────────────────────────────────────────────

interface DomainSectionProps {
  domainKey: DomainKey;
  label: string;
  description?: string;
  isNew?: boolean;
  goals: ReportGoalType[];
  selectedGoalIds: Set<string>;
  onGoalClick: (e: React.MouseEvent, goalId: string) => void;
  activeDragGoalId: string | null;
  onEdit?: () => void;
  onDelete?: () => void;
  deleteDisabled?: boolean;
  /** Optional grip slot rendered before the label — supplied by
   *  `SortableDomainSection` to initiate a domain-reorder drag. */
  gripHandle?: React.ReactNode;
}

const DomainSection: React.FC<DomainSectionProps> = ({
  domainKey: key,
  label,
  description,
  isNew,
  goals,
  selectedGoalIds,
  onGoalClick,
  activeDragGoalId,
  onEdit,
  onDelete,
  deleteDisabled,
  gripHandle,
}) => {
  const { isOver, setNodeRef } = useDroppable({ id: key });
  return (
    <div
      ref={setNodeRef}
      className={clsx("rounded-md border border-border-medium", isOver && "border-hatch-500 bg-hatch-50")}
    >
      <div className="flex items-center justify-between border-b border-border-medium px-3 py-2">
        <div className="flex items-center gap-2">
          {gripHandle}
          <span className="font-medium text-text-primary">{label}</span>
          {isNew && <span className="rounded-sm bg-green-100 px-2 py-0.5 text-xs text-green-700">New</span>}
          {/* Compact inline preview — single-line, markdown stripped. */}
          {description && (
            <>
              <Small colorType="secondary">—</Small>
              <DomainDescription content={description} previewMode className="min-w-0 truncate" />
            </>
          )}
          <Small colorType="secondary">({goals.length})</Small>
        </div>
        {(onEdit || onDelete) && (
          <div className="flex items-center gap-1">
            {onEdit && (
              <button
                type="button"
                aria-label={`Edit domain ${label}`}
                className="rounded-sm p-1 text-icon-secondary hover:bg-limestone-100 hover:text-text-primary"
                onClick={onEdit}
              >
                <PencilSquareIcon className="h-5 w-5" />
              </button>
            )}
            {onDelete && (
              <Tooltip
                showInfoIcon={false}
                disabled={!deleteDisabled}
                text="Move all goals out of this domain before deleting it."
                placement="left"
              >
                <button
                  type="button"
                  aria-label={`Delete domain ${label}`}
                  disabled={deleteDisabled}
                  className={clsx(
                    "rounded-sm p-1 text-icon-secondary",
                    deleteDisabled
                      ? "cursor-not-allowed text-icon-disabled"
                      : "hover:bg-limestone-100 hover:text-red-600",
                  )}
                  onClick={onDelete}
                >
                  <TrashIcon className="h-5 w-5" />
                </button>
              </Tooltip>
            )}
          </div>
        )}
      </div>
      <div className="space-y-1 p-2" data-testid={`domain-bucket-${key}`}>
        {goals.length === 0 ? (
          <Small colorType="secondary" className="px-2 py-2 italic">
            No goals.
          </Small>
        ) : (
          goals.map((g) => (
            <GoalRow
              key={g.id}
              goal={g}
              selected={selectedGoalIds.has(g.id)}
              isBeingDragged={activeDragGoalId === g.id}
              onClick={onGoalClick}
            />
          ))
        )}
      </div>
    </div>
  );
};

interface SortableDomainSectionProps extends DomainSectionProps {
  sortableId: string;
}

const SortableDomainSection: React.FC<SortableDomainSectionProps> = ({ sortableId, ...rest }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: sortableId });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 10 : undefined,
  };
  return (
    <div ref={setNodeRef} style={style}>
      <DomainSection
        {...rest}
        gripHandle={
          <button
            type="button"
            aria-label={`Reorder domain ${rest.label}`}
            data-manage-domains-grip="true"
            className="manage-domains-grip cursor-grab touch-none rounded-sm p-1 text-icon-secondary hover:bg-limestone-100 hover:text-text-primary"
            {...attributes}
            {...listeners}
          >
            <Bars3Icon className="h-5 w-5" />
          </button>
        }
      />
    </div>
  );
};

interface GoalRowProps {
  goal: ReportGoalType;
  selected: boolean;
  isBeingDragged: boolean;
  onClick: (e: React.MouseEvent, goalId: string) => void;
}

const GoalRow: React.FC<GoalRowProps> = ({ goal, selected, isBeingDragged, onClick }) => {
  const { attributes, listeners, setNodeRef } = useDraggable({ id: goal.id });
  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      data-goal-id={goal.id}
      className={clsx(
        "manage-domains-goal cursor-pointer select-none rounded-sm border border-transparent px-3 py-2 text-sm",
        selected
          ? "border-hatch-600 bg-hatch-100 text-text-primary outline outline-2 outline-hatch-600"
          : "hover:bg-limestone-50",
        isBeingDragged && "opacity-40",
      )}
      onClick={(e) => onClick(e, goal.id)}
    >
      {goalDisplayName(goal)}
    </div>
  );
};

interface DragOverlayCardProps {
  primaryGoalName: string;
  count: number;
}

const DragOverlayCard: React.FC<DragOverlayCardProps> = ({ primaryGoalName, count }) => (
  <div className="rounded-sm border border-hatch-400 bg-white px-3 py-2 text-sm shadow-md">
    {count > 1 ? `${count} goals` : primaryGoalName}
  </div>
);
